import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, Divider, LinearProgress, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { UserDetail } from '../../../../types/user';
import { useMasteredWords } from '../../../../hooks/useUser';
import { fmtDateOnly } from '../../userShowUtils';
import { PaginatedSection } from '../../userShowComponents';

/** ④-2. 단어장·마스터 — 보유 단어장 진행도 + 라이브러리 + 마스터 단어 목록(페이징) */
const WordbookMasteryTab: FC<{ user: UserDetail; userId: number }> = ({ user, userId }) => {
    const navigate = useNavigate();
    const [masteredPage, setMasteredPage] = useState(1);
    const recentWordbooks = user.recentWordbooks ?? [];
    const recentLibraries = user.libraryStats?.recentLibraries ?? [];
    const { data: masteredData, isLoading: masteredLoading } = useMasteredWords(userId, masteredPage - 1);
    const masteredWords = masteredData?.content ?? [];

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>보유 단어장</Typography>
            {recentWordbooks.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>학습 중인 단어장이 없습니다.</Typography>
            ) : (
                <TableContainer sx={{ mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>단어장</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>학습 진도</TableCell>
                                <TableCell>완료일</TableCell>
                                <TableCell>시작일</TableCell>
                                <TableCell align="right">상세</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recentWordbooks.map((w) => {
                                const progress = w.totalWords > 0 ? Math.round((w.learnedWords / w.totalWords) * 100) : 0;
                                const isCompleted = !!w.completedAt;
                                const isGroup = !!w.isGroupEntry && !!w.groupId;
                                const detailPath = isGroup ? `/wordbook-group/${w.groupId}` : `/wordbook/${w.wordbookId}`;
                                return (
                                    <TableRow key={w.wordbookId} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(detailPath)}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {isGroup ? (w.groupTitle || w.wordbookTitle) : w.wordbookTitle}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {isGroup ? `GROUP ID: ${w.groupId}` : `ID: ${w.wordbookId}`}
                                            </Typography>
                                            {isGroup && (
                                                <Box mt={0.5}>
                                                    <Chip size="small" label="그룹" color="secondary" variant="outlined" />
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={isCompleted ? '완료' : '진행 중'}
                                                color={isCompleted ? 'success' : 'info'}
                                                variant={isCompleted ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 220 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Box sx={{ width: 120 }}>
                                                    <LinearProgress
                                                        variant="determinate" value={progress}
                                                        color={isCompleted ? 'success' : 'primary'}
                                                    />
                                                </Box>
                                                <Typography variant="body2">{w.learnedWords} / {w.totalWords}</Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">완료율 {progress}%</Typography>
                                        </TableCell>
                                        <TableCell>{fmtDateOnly(w.completedAt)}</TableCell>
                                        <TableCell>{fmtDateOnly(w.createdAt)}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small" endIcon={<OpenInNewIcon />}
                                                onClick={(e) => { e.stopPropagation(); navigate(detailPath); }}
                                            >보기</Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>라이브러리</Typography>
            {recentLibraries.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>연결된 라이브러리가 없습니다.</Typography>
            ) : (
                <TableContainer sx={{ mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>항목</TableCell>
                                <TableCell>유형</TableCell>
                                <TableCell>획득일</TableCell>
                                <TableCell align="right">상세</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recentLibraries.map((item) => (
                                <TableRow key={`${item.itemType}-${item.libraryId}`}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            LIBRARY ID: {item.libraryId} · {item.totalWords.toLocaleString()}단어
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={item.itemType === 'GROUP' ? '그룹' : '단일'}
                                            color={item.itemType === 'GROUP' ? 'secondary' : 'info'}
                                            variant="outlined"
                                        />
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {item.libraryType}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{fmtDateOnly(item.acquiredAt)}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small" endIcon={<OpenInNewIcon />}
                                            onClick={() => navigate(`/library/${item.libraryId}`)}
                                        >보기</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>마스터 단어 ({user.masteredWordCount.toLocaleString()})</Typography>
            <PaginatedSection
                loading={masteredLoading} empty={masteredWords.length === 0} emptyText="마스터한 단어가 없습니다."
                page={masteredPage} totalPages={masteredData?.totalPages ?? 0} onPageChange={setMasteredPage}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>단어</TableCell>
                            <TableCell>뜻</TableCell>
                            <TableCell>언어</TableCell>
                            <TableCell>숙련도</TableCell>
                            <TableCell>마스터 일시</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {masteredWords.map((m) => (
                            <TableRow key={m.dictId}>
                                <TableCell><Typography variant="body2" fontWeight={600}>{m.word}</Typography></TableCell>
                                <TableCell>{m.meaning ?? '-'}</TableCell>
                                <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                        {m.headLang} → {m.meaningLang}
                                    </Typography>
                                </TableCell>
                                <TableCell>{m.masteryScore ?? '-'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateOnly(m.masteredAt)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PaginatedSection>
        </Box>
    );
};

export default WordbookMasteryTab;
